import { NotificationType, type NurseFullRole, type OpenShiftRequest, type ReplaceShiftRequest } from "../../../../utils/models";
import { rolesTakingRequest } from "../../../../utils/roles";
import type { GapsScopedShiftGroup } from "../gapsGroupSheets";
import type { GapsPostTimes } from "./gapsPostShiftPrefill";

/**
 * What an add is FOR, resolved once the sheet knows the group: the shift window, the unit (if one was chosen), the
 * hours, and the posted request it completes (if any). Everything the candidate list, the warning gates and the write
 * read comes from here, so they cannot disagree about which shift the person is being added to.
 */
export type GapsAddContext = {
	group: GapsScopedShiftGroup;
	/**
	 * The unit the person goes into — the missing row's — or `null` when the add started from the group picker and no
	 * unit was chosen yet; the assign-unit sheet follows such an add.
	 */
	locationName: string | null;
	/**
	 * The hours the person is added for, fixed: the missing row's own slot, the group's window, or — completing a
	 * posted request — the request's own hours, which is what completing it commits the person to.
	 */
	times: GapsPostTimes;
	/** The posted request this add fills (P3-8), or `null`. */
	linkedRequest: OpenShiftRequest | null;
};

/** The job the added shift carries: the request's for a linked add, else the section's (`null` = a role section). */
export const jobIdForAdd = ({ linkedRequest, group }: Pick<GapsAddContext, "linkedRequest" | "group">): string | undefined => {
	if (linkedRequest !== null) return linkedRequest.job_id;
	return group.scope.jobId === null ? undefined : group.scope.jobId;
};

/**
 * The roles whose staff the add offers, before `jobIdForAdd` narrows them to a job's holders: a linked request's own
 * (`rolesTakingRequest` — every role in a job department, or for a request naming none or "all"), a job section's
 * every role (`departmentRoles`), or a role section's roles.
 */
export const rolesForAdd = (
	{ linkedRequest, group }: Pick<GapsAddContext, "linkedRequest" | "group">,
	departmentRoles: readonly NurseFullRole[],
	jobManagementEnabled: boolean,
): readonly NurseFullRole[] => {
	if (linkedRequest !== null) return rolesTakingRequest(linkedRequest.message.roles, departmentRoles, jobManagementEnabled);
	return group.scope.jobId !== null ? departmentRoles : group.scope.roles;
};

/** An add sheet's link to a posted request, as it stands now. */
export type GapsAddLink = {
	/** The request the add completes, or `null` for an unlinked add. */
	linkedRequestId: string | null;
	/** That request as last seen (held after it leaves the list, so the sheet still describes it); `null` before it is seen. */
	linkedRequest: OpenShiftRequest | null;
	/**
	 * Linked, and not in the live list NOW: never found, or gone since (filled, deleted, merged away). The candidates
	 * step refuses the add as gone unless it is its own add that closed it.
	 */
	isLost: boolean;
};

/**
 * What an add sheet's link to a posted request comes to (P3-8, M7, M8), from the id it was opened with, the request as
 * the live list has it now, and as the sheet last saw it.
 *
 * Only an OPEN SHIFT can be completed by an add (`completeManualAdd`). The desktop's hole matcher also ties a hole to a
 * replacement or a swap — they carry the same `needed_nurses` key — and such a hole is filled like any other: the add
 * goes ahead UNLINKED, scoped to the hole, rather than being refused as "request not found". A request that is not in
 * the list now is lost, however long ago it was seen: holding it kept the add enabled after the request was gone, and
 * the add then wrote a person and failed to complete anything.
 */
export const resolveAddLink = ({
	linkedRequestId,
	live,
	held,
}: {
	linkedRequestId: string | null;
	live: OpenShiftRequest | ReplaceShiftRequest | undefined;
	held: OpenShiftRequest | ReplaceShiftRequest | undefined;
}): GapsAddLink => {
	if (linkedRequestId === null || (held !== undefined && held.request_type !== NotificationType.OpenShift)) {
		return { linkedRequestId: null, linkedRequest: null, isLost: false };
	}
	// A request whose type is OpenShift IS an open-shift request; the context list is typed as the union of every kind.
	return { linkedRequestId, linkedRequest: held === undefined ? null : (held as OpenShiftRequest), isLost: live === undefined };
};
